import {
  isPlaylistImport,
  type ImportRequest,
  type ImportResult,
  type JobDto,
  type PlaylistImportResult,
} from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui';
import { api } from '../../lib/api';

function useJob(jobId: string | null) {
  return useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api<JobDto>(`/api/admin/jobs/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (q) =>
      q.state.data && ['done', 'failed'].includes(q.state.data.state) ? false : 1000,
  });
}

function Result({ job }: { job: JobDto }) {
  if (job.state === 'failed') {
    return (
      <p role="alert" className="m-0 text-[14px] text-mistake">
        {job.error ?? 'The import failed.'}
      </p>
    );
  }
  if (job.state !== 'done') return <p className="m-0 text-[14px] text-muted">Importing…</p>;
  const result = job.result as ImportResult | PlaylistImportResult;
  if (isPlaylistImport(result)) {
    return (
      <p className="m-0 text-[14px]">
        {result.queued} videos from the playlist are queued; each one shows under Recent imports.
      </p>
    );
  }
  const r = result;
  return (
    <div className="flex flex-col gap-2 text-[14px]">
      <p className="m-0">
        <strong>{r.title}</strong>{' '}
        {r.duplicate
          ? 'was already in the library.'
          : r.status === 'ready'
            ? 'is in the library.'
            : 'is in the library, but needs a plugin.'}
      </p>
      <p className="m-0 text-muted">
        Format: {r.format} ·{' '}
        {r.status === 'ready' ? 'one solution, verified' : 'not verified: rules need mapping'}
      </p>
      {r.unmapped.length > 0 && (
        <div>
          <p className="m-0 font-semibold">Unmapped elements (kept as drawings)</p>
          <ul className="m-0 pl-5">
            {r.unmapped.map((u, i) => (
              <li key={i}>
                {u.kind}
                {u.detail ? ` (${u.detail})` : ''} × {u.count}
              </li>
            ))}
          </ul>
        </div>
      )}
      <Link to={`/puzzle/${r.puzzleId}`} className="font-semibold">
        Open the puzzle
      </Link>
    </div>
  );
}

/** Admin → Import: paste a SudokuPad, f-puzzles or Penpa+ link, a video, a playlist (every video
 * in it) or a digit string, and the rules when the puzzle doesn't carry them (older Cracking the
 * Cryptic videos say them aloud). */
export function AdminImportPage() {
  const [text, setText] = useState('');
  const [rules, setRules] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const client = useQueryClient();
  const job = useJob(jobId);
  const start = useMutation({
    mutationFn: (body: ImportRequest) =>
      api<{ jobId: string }>('/api/admin/import', { method: 'POST', json: body }),
    onSuccess: (r) => {
      setJobId(r.jobId);
      void client.invalidateQueries({ queryKey: ['library'] });
    },
  });
  const recent = useQuery({
    queryKey: ['jobs', 'import', job.data?.state],
    queryFn: () => api<{ jobs: JobDto[] }>('/api/admin/jobs?type=import'),
  });
  // Every failure, however old, with what failed and why (Marko, 2026-11-04).
  const failed = useQuery({
    queryKey: ['jobs', 'import', 'failed', job.data?.state],
    queryFn: () => api<{ jobs: JobDto[] }>('/api/admin/jobs?type=import&state=failed'),
  });

  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Import</h1>
      <Card className="flex max-w-[720px] flex-col gap-3 px-4 py-4">
        <label htmlFor="import-text" className="text-[14px] font-semibold">
          Puzzle link or code
        </label>
        <textarea
          id="import-text"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="A SudokuPad, f-puzzles or Penpa+ link, a YouTube video or playlist, or 81 digits"
          className="rounded-btn border border-line bg-surface p-3 font-sans text-[14px] text-ink"
        />
        <label htmlFor="import-rules" className="text-[14px] font-semibold">
          Rules <span className="font-normal text-muted">(optional)</span>
        </label>
        <textarea
          id="import-rules"
          rows={3}
          value={rules}
          onChange={(e) => setRules(e.target.value)}
          aria-describedby="import-rules-hint"
          placeholder="Normal sudoku rules apply. Cells a knight's move apart can't contain the same digit…"
          className="rounded-btn border border-line bg-surface p-3 font-sans text-[14px] text-ink"
        />
        <p id="import-rules-hint" className="m-0 -mt-1 text-[13px] text-muted">
          For puzzles that come without rules, such as older Cracking the Cryptic videos. Read like
          any puzzle&apos;s rules; the puzzle&apos;s own rules come first.
        </p>
        <div>
          <button
            type="button"
            disabled={text.trim() === '' || start.isPending}
            onClick={() =>
              start.mutate({ text: text.trim(), ...(rules.trim() ? { rules: rules.trim() } : {}) })
            }
            className="h-11 rounded-btn border-0 bg-accent px-5 text-[15px] font-bold text-white disabled:opacity-50"
          >
            Import
          </button>
        </div>
        {start.isError && (
          <p role="alert" className="m-0 text-[14px] text-mistake">
            {start.error.message}
          </p>
        )}
        {job.data && <Result job={job.data} />}
      </Card>
      {(failed.data?.jobs.length ?? 0) > 0 && (
        <section className="flex max-w-[720px] flex-col gap-2" aria-label="Failed imports">
          <h2 className="m-0 text-[16px] font-bold">Failed imports ({failed.data!.jobs.length})</h2>
          {failed.data!.jobs.map((j) => (
            <div
              key={j.id}
              data-failed-import
              className="flex items-start gap-3 rounded-btn border border-line bg-surface px-3 py-2 text-[13px]"
            >
              <span className="w-20 shrink-0 text-muted">
                {new Date(j.createdAt).toLocaleDateString('en-GB')}
              </span>
              <Failure job={j} />
            </div>
          ))}
        </section>
      )}
      <section className="flex max-w-[720px] flex-col gap-2">
        <h2 className="m-0 text-[16px] font-bold">Recent imports</h2>
        {(recent.data?.jobs ?? []).map((j) => {
          const result = j.result as ImportResult | PlaylistImportResult | null;
          const r = isPlaylistImport(result) ? null : result;
          return (
            <div
              key={j.id}
              className="flex items-start gap-3 rounded-btn border border-line bg-surface px-3 py-2 text-[13px]"
            >
              <span className="w-20 shrink-0 text-muted">
                {new Date(j.createdAt).toLocaleDateString('en-GB')}
              </span>
              {j.state === 'failed' ? (
                <Failure job={j} />
              ) : (
                <span className="min-w-0 flex-1 truncate">
                  {j.state === 'done' && isPlaylistImport(result) ? (
                    `Playlist: ${result.queued} videos queued`
                  ) : j.state === 'done' && r ? (
                    <Link to={`/puzzle/${r.puzzleId}`}>{r.title}</Link>
                  ) : (
                    'Importing…'
                  )}
                </span>
              )}
              <span className="text-muted">{r?.status ?? j.state}</span>
            </div>
          );
        })}
      </section>
    </>
  );
}

/** A failed import: what it was given (the video or link, opened in a new tab) and the whole
 * reason, wrapped rather than cut. */
function Failure({ job }: { job: JobDto }) {
  const link = job.input?.startsWith('http') ? job.input : null;
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      {job.input &&
        (link ? (
          <a href={link} target="_blank" rel="noreferrer" className="truncate">
            {job.inputTitle ?? link}
          </a>
        ) : (
          <span className="truncate text-muted">{job.input}</span>
        ))}
      <span className="break-words text-mistake">{job.error}</span>
    </span>
  );
}
